import React, { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import PageLayout from '../pagelayout';
import Search from '../components/calendar/search';
import Calendar from '../components/calendar';
import * as css from './calendaruha.module.scss';
import { ActivityStatusOptions } from './calendarModels';
import classNames from 'classnames';
import { DropdownButton } from '../components/button/dropdownButton';
import useCalendarNavigation, {
  CalendarNavigationContext,
  ZoomFactor,
} from '../components/calendar/useCalendarNavigation';
import {
  Routes,
  Route,
  Outlet,
  useNavigate,
  useParams,
} from 'react-router-dom';
import Activity from '../practiceactivity/activity.uha';
import PageNotFoundPage from '../pages/pagenotfound';
import { getAndSetCurrentYearFromSessionStorage } from '../components/calendar/utils';
import CalendarDownload, {
  CalendarDownloadApiType,
} from '../components/calendar/calendardownload';

const CalendarAndActivityPage = () => (
  <Routes>
    <Route path="/" element={<Outlet />}>
      <Route path=":startYear?" element={<CalendarPage />} />
      <Route path="activity/:activityId/*" element={<Activity />} />
      <Route path="*" element={<PageNotFoundPage />} />
    </Route>
  </Routes>
);

const CalendarPage = () => {
  const { formatMessage } = useIntl();
  const { startYear } = useParams<{ startYear: string }>();
  const currentYear = getAndSetCurrentYearFromSessionStorage(startYear);
  const [searchPhrase, setSearchPhrase] = React.useState(
    sessionStorage.getItem('UHACalendarSearchPhrase') || '',
  );
  const [searchCssOutlined, setSearchCssOutlined] = React.useState(false);
  const [searchClearButton, setSearchClearButton] = React.useState(
    searchPhrase.length > 0,
  );
  const [activityStatusFilter, setActivityStatusFilter] = React.useState(
    (sessionStorage.getItem(
      'UHAActivityStatusFilter',
    ) as ActivityStatusOptions) || ActivityStatusOptions.UNFILTERED,
  );
  const navigate = useNavigate();
  const [calendarNavigation, calendarNavigationDispatch] =
    useCalendarNavigation();

  const cellClick = (activityId: string, isNewActivity: boolean = false) => {
    navigate(
      `/calendar/activity/${activityId}${isNewActivity ? '?create=true' : ''}`,
    );
  };

  const linkYear = formatMessage({
    id: 'calendaruha.linkYear',
    defaultMessage: 'Year',
  });
  const pageTitle1 = formatMessage({
    id: 'calendaruha.titlePart1',
    defaultMessage: '[!] Practice Calendar',
  });
  const pageTitle2 = formatMessage(
    { id: 'calendaruha.titlePart2' },
    { year: `${currentYear}/${currentYear + 1}` },
  );
  const emptyStateTitle = formatMessage({
    id: 'calendaruha.emptyStateTitle',
  });
  const emptyStateText = formatMessage({
    id: 'calendaruha.emptyStateText',
  });

  const pageSubtitle = formatMessage({
    id: 'calendaruha.subtitle',
    defaultMessage:
      '[!] Overview of the periods in which students will attend a workplace experience',
  });

  const handleMenuClick = (year: number) => {
    navigate(`/calendar/${year}`);
  };

  const titleDropdownElements = [
    {
      text: `${linkYear} ${currentYear - 1}/${currentYear}`,
      onChange: () => handleMenuClick(currentYear - 1),
    },
    {
      text: `${linkYear} ${currentYear}/${currentYear + 1}`,
      onChange: () => {},
      disabled: true,
    },
    {
      text: `${linkYear} ${currentYear + 1}/${currentYear + 2}`,
      onChange: () => handleMenuClick(currentYear + 1),
    },
  ];

  const titleAction = (
    <DropdownButton
      id={'calendartitle'}
      btnText={pageTitle2}
      options={titleDropdownElements}
    />
  );

  useEffect(
    () =>
      sessionStorage.setItem('UHAActivityStatusFilter', activityStatusFilter),
    [activityStatusFilter],
  );

  useEffect(
    () => sessionStorage.setItem('UHACalendarSearchPhrase', searchPhrase),
    [searchPhrase],
  );

  const actionItems = useMemo(
    () => (
      <CalendarDownload
        startYear={currentYear}
        api={CalendarDownloadApiType.StudiestedApi}
      />
    ),
    [currentYear],
  );

  return (
    <PageLayout
      documentTitle={`${pageTitle1} ${pageTitle2}`}
      isIndented={false}
      title={pageTitle1}
      titleActions={titleAction}
      actionItems={actionItems}
      subtitle={pageSubtitle}
      isFullScreen
    >
      <div
        className={classNames(
          css.calendarContentsWrapper,
          calendarNavigation.zoomFactor === ZoomFactor.WEEKLY
            ? 'weekly'
            : 'daily',
        )}
      >
        <CalendarNavigationContext.Provider value={calendarNavigationDispatch}>
          <Search
            onSearchPhraseChanged={(val: string) => setSearchPhrase(val)}
            searchPhrase={searchPhrase}
            navigation={calendarNavigation}
            searchCssOutlined={searchCssOutlined}
            changeSearchCssOutlined={setSearchCssOutlined}
            showClearButton={searchClearButton}
            toggleClearButton={setSearchClearButton}
            onActivityStatusFilterChanged={setActivityStatusFilter}
            activityStatusFilter={activityStatusFilter}
            isStudiested
          />
          <Calendar
            emptyStateTitle={emptyStateTitle}
            emptyStateText={emptyStateText}
            startYear={currentYear}
            navigation={calendarNavigation}
            searchPhrase={searchPhrase}
            onCellClick={cellClick}
            activityStatusFilter={activityStatusFilter}
            onActivityStatusFilterChanged={setActivityStatusFilter}
          />
        </CalendarNavigationContext.Provider>
      </div>
    </PageLayout>
  );
};

export default CalendarAndActivityPage;
